import { registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClient } from "@angular/common/http";
import localeFr from "@angular/common/locales/fr";
import { NgModule } from "@angular/core";
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from "ngx-mask";
import { OdqToolboxModule } from "odq-toolbox";
import { environment } from "../environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { CoreModule } from "./core/core.module";
import { LoaderInterceptor } from "./core/loader";
import { LocalizedDatePipe } from "./core/locale/localized-date.pipe";
import { LogService } from "./core/logger";
import { LoggerModule } from "./core/logger/logger.module";
import { HomeModule } from "./home/home.module";
import { MessageInfoComponent } from "./message-info/message-info.component";
import { PublicMemberSearchModule } from "./public-member-search/public-member-search.module";
import { LanguageService } from "./services/language.service";
import { FuseTranslationLoaderService } from "./services/translation-loader.service";
import { BadBrowserPageComponent } from "./shared/bad-browser-page/bad-browser-page.component";
import { SharedModule } from "./shared/shared.module";
import { PaymentModule } from './payment/payment.module';

const disableAnimations =
  !("animate" in document.documentElement) ||
  (navigator && /iPhone OS (8|9|10|11|12|13)_/.test(navigator.userAgent));

@NgModule({
  declarations: [AppComponent, MessageInfoComponent, BadBrowserPageComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule.withConfig({ disableAnimations }),
    CoreModule,
    OdqToolboxModule.forRoot(
      FuseTranslationLoaderService,
      LanguageService,
      environment.canadaPostApiKey,
      "",
      environment.projectionQueryApi
    ),
    TranslateModule.forRoot(),
    LoggerModule.forRoot({
      publisher: [
        {
          loggerName: "console",
          isActive: !environment.production,
          loggerLocation: "",
        },
        {
          loggerName: "localstorage",
          isActive: false,
          loggerLocation: "odqLogging",
        },
        {
          loggerName: "webapi",
          isActive: false,
          loggerLocation: "http://localhost:4201/api/log",
        },
      ],
    }),
    AppRoutingModule,
    SharedModule,
    HomeModule,
    PublicMemberSearchModule,
    PaymentModule,
    NgxMaskDirective,
    NgxMaskPipe,
  ],
  providers: [
    // Interceptors are a pipeline and the order they are provided in matters in determining the order
    // they get access to the request object (to modify or inspect) and the response object (to modify or inspect).
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    LocalizedDatePipe,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: "outline" },
    },
    provideNgxMask(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(logger: LogService) {
    registerLocaleData(localeFr, "fr");
    logger.clear();
    logger.log("APP STARTING...");
  }
}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader();
}
